<template>
  <div
    class="modal search-modal"
    @mousedown="hidePopup"
  >
    <div
      class="popup-window--search-window rounded-md mr-auto mb-auto ml-auto overflow-hidden"
      @mousedown.stop
    >
      <div
        class="search-input flex items-center"
        :class="{ 'border-solid border-0 border-b border-dividers-default': showAskAiNotesModal }"
        @click.stop="focusSearchField"
      >
        <div class="search-input__icon search-input__icon--left box-border pl-1 usn relative">
          <em
            class="bg-icon-default m-auto block"
            :style="getCustomIconStyle(iconPath('search-alternative'))"
          />
        </div>
        <div class="search-input__control flex-grow">
          <input
            ref="searchTextInput"
            v-model="searchQuery"
            :placeholder="
              showAskAiNotesModal
                ? translate('searchPlaceholderAskAiNotes')
                : translate('searchPlaceholder')
            "
            class="search-input__field border-0 bg-transparent text-text-default placeholder-text-placeholder w-full border-box focus:outline-none"
            @input="handleInputChange"
            @keyup.enter="handleEnter"
            @keyup.esc="handleEsc"
          />
        </div>
        <ButtonCustom
          v-if="showAskAiNotesModal"
          class="btn attention mr-2 rounded"
          type="important"
          :variation="'small'"
          :text="translate('Ask')"
          :custom-icon-name="'send'"
          :custom-icon-color="'var(--iconBg)'"
          :custom-icon-size="'16px'"
          @click="askAi"
        />
      </div>
      <div
        v-if="!aiGeneralSearch.open && !showAskAiNotesModal"
        class="flex flex-row border-0 border-t border-solid border-dividers-default gap-2 px-4 py-2"
        :class="{ 'flex-wrap': isMobile }"
      >
        <SearchPill
          ref="typeSearchPill"
          :icon="'search-alternative'"
          :label="translate('type')"
          :plural-label="translate('types')"
          :show-search-input="false"
          :dropdown-options="typeDropdownOptions"
          class="shrink-0"
          @value-change="typeValueChange"
          @dropdown-closed="searchPillDropdownClosed"
        />
        <SearchPill
          :icon="'circle'"
          :label="translate('cardStatus')"
          :plural-label="translate('statuses')"
          :show-search-input="false"
          :dropdown-options="getStatusDropdownOptions"
          class="shrink-0"
          @value-change="statusValueChange"
          @dropdown-closed="searchPillDropdownClosed"
        />
        <SearchPill
          :icon="'folder'"
          :label="translate('space')"
          :plural-label="translate('spaces')"
          :dropdown-options="spaceDropdownOptions"
          :dropdown-placeholder="translate('space___')"
          @value-change="spaceValueChange"
          @dropdown-closed="searchPillDropdownClosed"
        />
        <SearchPill
          :icon="'paper-box'"
          :label="translate('option.label.showArchived')"
          :single-value="true"
          :selected-value="searchParams.archived"
          @value-change="archivedChange"
          @dropdown-closed="searchPillDropdownClosed"
        />
        <ButtonCustom
          v-if="filtersNotDefault"
          v-tooltip="{
            content: translate('resetFilters'),
            placement: 'top',
          }"
          :type="'transparent hover:bg-hover-onHover p-0 ml-auto'"
          :custom-icon-name="'x'"
          :custom-icon-color="'--var(iconDefault)'"
          :custom-icon-size="'16px'"
          :custom-icon-props="{ margin: 0 }"
          :button-dimensions="{ width: 22, height: 22 }"
          @click="resetFilters"
        />
      </div>
      <div
        v-if="shouldShowAskAiSection"
        class="ask-ai flex items-center py-2 px-4 border-solid border-0 border-t border-b border-dividers-default -mt-px pt"
        @click="handleAskAiClick"
      >
        <em
          class="custom-icons"
          :style="getCustomIconStyle(iconPath(aiGeneralSearch.open ? 'chevron-left' : 'zap'))"
        />
        <div class="text-sm text-text-default ml-2">
          {{
            !aiGeneralSearch.open
              ? translate('askAiAssistantSearch')
              : translate('backToSearchResults')
          }}
        </div>
        <em
          v-if="!aiGeneralSearch.open"
          class="custom-icons ml-auto"
          :style="getCustomIconStyle(iconPath('chevron-right'))"
        />
      </div>
      <SearchAiResults
        v-if="aiGeneralSearch.open && !showAskAiNotesModal"
        :ai-generated-text="aiGeneralSearch.generatedText"
        :ai-generic-links="aiGeneralSearch.genericLinks"
        @hide-search-popup="hidePopup"
      />
      <SearchAiResults
        v-if="aiNotesSearch.open && showAskAiNotesModal"
        :ai-generated-text="aiNotesSearch.generatedText"
        :ai-generic-links="aiNotesSearch.genericLinks"
        @hide-search-popup="hidePopup"
      />
      <div
        v-if="!aiGeneralSearch.open && !showAskAiNotesModal"
        class="search-results flex flex-col overflow-hidden -mt-px"
        @mousemove="mouseMoveHandle"
        @click.stop
      >
        <div
          v-if="!searchResultsLength && searchQuery && responseQuery"
          class="search-results__no-results usn"
        >
          {{ translate('noResultsFound') }}
        </div>
        <SearchResults
          v-if="showSearchModal"
          ref="searchResults"
          :results="results"
          :results-label="resultsLabel"
          :is-grouped="false"
          :search-query="searchQuery"
          :type="$constants.searchResultsType.SearchModal"
          @selectSearchResult="selectSearchResult"
          @labelCliked="labelClikedHandler"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, defineAsyncComponent } from 'vue';
import useSetAndOpenProject from '@/utilities/composables/useSetAndOpenProject';
import getDashedTitle from '@/utilities/getDashedTitle';
import customIconsMixin from '@/mixins/customIconsMixin';
import { getCardStatus } from '@/utilities/defaultCardStatuses';
import cardStatuses from '@/mixins/cardStatuses';
import { SpfIcons } from '@/types/icons';
import { ISearchResults, ITeamProject, StoreActionName } from '@/store/model';
import { browserStorageGetItem, browserStorageSetItem } from '@/utilities/browser-storage.util';
import { SearchType, searchModalFilterStorageKey } from '@/types/search';
import EventBus from '@/utilities/eventBus';
import useStreamResponseReader from '@/utilities/composables/useStreamResponseReader';
import SearchAiResults from '@/components/elements/SearchAiResults.vue';
import { ResourceType } from '../../types/resources';
import SearchPill from '@/components/elements/SearchPill.vue';
import ButtonCustom from '@/components/elements/ButtonCustom.vue';
import isEqual from 'lodash/isEqual';
import useStatusDropdownOptions from '@/utilities/composables/useStatusDropdownOptions';
import Fuse, { FuseResult } from 'fuse.js';
import cardStatusType from '@/types/cardStatusType';
import cloneDeep from 'lodash/cloneDeep';
import useCurrentRole from '@/utilities/composables/useCurrentRole';
import useEpicFeature from '@/utilities/composables/useEpicFeature';

const SearchResults = defineAsyncComponent(
  () => import(/* webpackChunkName: "searchResults" */ '@/components/elements/SearchResults.vue')
);

const defaultStatuses = [
  getCardStatus(cardStatusType.Backlog).id,
  getCardStatus(cardStatusType.Committed).id,
  getCardStatus(cardStatusType.Started).id,
];

const defaultFilters = {
  archived: false,
  statuses: [...defaultStatuses],
  types: [],
  spaces: [],
};

interface ISearchModalData {
  searchQuery: string;
  selectedIndex: number;
  responseQuery: boolean;
  searchParams: {
    archived: boolean;
    types: SearchType[];
    statuses: string[];
    spaces: any[];
  };
  suspendMouseEnter: boolean;
  scrollTop: boolean;
  aiGeneralSearch: {
    open: false;
    loading: false;
    generatedText: '';
    genericLinks: any[];
  };
  aiNotesSearch: {
    open: false;
    loading: false;
    generatedText: '';
    genericLinks: any[];
  };
  lastAiGeneralSearchState: {
    open: false;
    generatedText: '';
    genericLinks: any[];
  };
  askAiLoading: boolean;
  resourceTypes: any[];
}
interface ISearchModalFilterOptions {
  searchParamsTypes: SearchType[];
  searchParamsArchived: boolean;
  searchParamsSpaces: any[];
  searchParamsStatuses: string[];
}
export default defineComponent({
  name: 'SearchModal',
  components: {
    SearchResults,
    SearchAiResults,
    SearchPill,
    ButtonCustom,
  },
  mixins: [customIconsMixin, cardStatuses],
  enums: {
    SpfIcons,
  },
  emits: ['mouseMove', 'disableMouseEnter'],
  setup() {
    const { setAndOpenProject } = useSetAndOpenProject() as any;
    const { convertStreamResponse } = useStreamResponseReader();
    const { statusDropdownOptions } = useStatusDropdownOptions() as any;
    const { isGuestReader } = useCurrentRole();
    const { epicFeatureAvailable, allowEpicInteraction, epicUpgradeBadge } = useEpicFeature();
    return {
      convertStreamResponse,
      setAndOpenProject,
      statusDropdownOptions,
      isGuestReader,
      allowEpicInteraction,
      epicUpgradeBadge,
      epicFeatureAvailable,
    };
  },

  data(): ISearchModalData | any {
    return {
      searchQuery: '',
      selectedIndex: -1,
      responseQuery: false,
      searchParams: cloneDeep(defaultFilters),
      suspendMouseEnter: false,
      scrollTop: false,
      aiGeneralSearch: {
        open: false,
        loading: false,
        generatedText: '',
        genericLinks: [],
      },
      aiNotesSearch: {
        open: false,
        loading: false,
        generatedText: '',
        genericLinks: [],
      },
      lastAiGeneralSearchState: {
        open: false,
        generatedText: '',
        genericLinks: [],
      },
      askAiLoading: false,
      resourceTypes: [
        {
          id: 'cards',
          searchKey: 'cards',
          icon: 'new-card-icon',
          customIcon: 'new-card-icon',
        },
        {
          id: 'boards',
          searchKey: 'boards',
          icon: 'board',
          customIcon: 'board',
        },
        {
          id: 'pages',
          searchKey: 'pages',
          icon: 'page',
          customIcon: 'page',
        },
        {
          id: 'notes',
          searchKey: 'notes',
          icon: 'note',
          customIcon: 'note',
        },
        {
          id: 'spaces',
          searchKey: 'projects',
          icon: 'folder',
          customIcon: 'folder',
        },
        ...(!this.isGuestReader
          ? [
              {
                id: 'epics',
                searchKey: 'epics',
                icon: 'box',
                customIcon: 'box',
              },
            ]
          : []),
        {
          id: 'members',
          searchKey: 'members',
          icon: 'user',
          customIcon: 'user',
        },
      ],
      fuse: null,
    };
  },
  computed: {
    currentBoardTitle() {
      return this.$store.getters.currentBoard.title;
    },
    currentBoardId() {
      return this.$store.getters.currentBoardId;
    },
    results() {
      if (this.showRecents) {
        return this.recentlyViewedResults;
      }
      const results = this.searchResults.results || [];
      return results;
    },
    showRecents() {
      return (
        !this.searchResults.results?.length && this.hasRecentlyViewedResults && !this.searchQuery
      );
    },
    resultsLabel() {
      return this.showRecents ? 'electronRecentlyViewed' : 'results';
    },
    showSearchModal() {
      return this.$store.getters.showSearchModal;
    },
    showAskAiNotesModal() {
      return this.$store.getters.showAskAiNotesModal;
    },
    typeDropdownOptions() {
      return this.resourceTypes.map((resourceType: any) => {
        return {
          icon: resourceType.icon,
          customIcon: resourceType.customIcon,
          customIconColor: 'var(--iconDefault)',
          key: resourceType.id,
          id: resourceType.id,
          searchKey: resourceType.searchKey,
          label: this.$capitalizeFirstLetter(this.translate(resourceType.id)),
          checkbox: true,
          checked: this.searchParams.types?.includes?.(resourceType.searchKey),
          lighter: true,
          strong: true,
          ...(resourceType.id === SearchType.epics && {
            ...this.epicUpgradeBadge,
            disabledCallback: () => {
              this.$refs.typeSearchPill?.$refs?.filterPill?.hide?.();
              this.allowEpicInteraction();
            },
          }),
        };
      });
    },
    getStatusDropdownOptions() {
      return this.statusDropdownOptions(this.cardStatuses, this.searchParams.statuses);
    },
    spaceDropdownOptions() {
      return this.teamProjects.map((project: any) => ({
        icon: project.icon?.src,
        customIcon: project.icon?.src,
        iconType: project.icon?.type,
        customIconColor: project.icon?.color,
        key: project.id,
        id: project.id,
        label: project.title,
        checkbox: true,
        checked: this.searchParams.spaces?.includes?.(project.id),
        lighter: true,
        strong: true,
        privateItem: project?.is_private,
      }));
    },
    searchResults(): ISearchResults {
      const searchResults = this.$store.getters.searchResults || {};

      let results = searchResults.results || [];
      if (this.searchParams.types.length === 0 || this.searchParams.types.includes('members')) {
        const membersResults = this.getMembersResults(this.searchQuery);

        results =
          this.searchParams.types.length === 1 && this.searchParams.types[0] === 'members'
            ? membersResults // if only members are selected, overwrite results
            : membersResults.concat(results);
      }

      return { ...searchResults, results };
    },
    teamSlug(): string {
      return this.$store.getters.getTeamSlug;
    },
    teamProjects(): ITeamProject[] {
      return this.$store.getters.getTeamProjects;
    },
    searchResultsLength(): number {
      return this.results?.length;
    },
    searchQueryLength(): number {
      return this.searchQuery?.length;
    },
    currentProjectId(): string {
      return this.$store.getters.getCurrentProjectId;
    },
    favouritesOpened(): any {
      return this.$store.getters.getFavouritesOpened;
    },
    flatBoards(): any {
      return this.$store.getters.getFlatBoards;
    },
    aiAssistantEnabled() {
      return this.$store.getters.getUserFeatureEnabled(this.$constants.featureFlagIds.aiAssistant);
    },
    hasRecentlyViewedResults(): boolean {
      return this.recentlyViewedResults.length > 0;
    },
    recentlyViewedResults() {
      const recentlyViewed = this.$store.getters.getElectronRecentlyViewed;
      const results: { [key: number]: any }[] = [];
      const routeNamesToTypes: { [key: string]: any } = {
        [this.$cons.routeNames.page]: this.$cons.routeNames.page,
        [this.$cons.routeNames.board]: this.$cons.routeNames.board,
        [this.$cons.routeNames.view]: this.$cons.routeNames.view,
        [this.$cons.epicRoute.name]: ResourceType.Epic,
        [this.$cons.routeNames.boardCard]: 'card',
        [ResourceType.Member]: ResourceType.Member,
        [ResourceType.Note]: ResourceType.Note,
        boards: 'project',
        pages: 'project',
      };
      if (recentlyViewed.length) {
        recentlyViewed.forEach((item: any) =>
          results.push({
            [routeNamesToTypes[item.type]]: { ...item, title: item.label },
          })
        );
      }
      return results;
    },
    filtersNotDefault(): boolean {
      return !isEqual(this.searchParams, defaultFilters);
    },
    userId() {
      return this.$store.getters.getUserId;
    },
    myWorkTabId() {
      return this.$store.getters.getMyWorkSelectedTabId(this.userId);
    },
    membersResults() {
      return Object.values(this.$store.getters.getTeamMembers || {});
    },
    shouldShowAskAiSection() {
      return (
        (this.searchQueryLength > 0 || this.aiGeneralSearch.open) &&
        this.aiAssistantEnabled &&
        !this.showAskAiNotesModal
      );
    },
  },
  watch: {
    showSearchModal(newValue) {
      if (newValue) {
        this.handleSearchModalOpen();
      } else {
        this.blurSearchField();
      }
    },
    membersResults(newValue) {
      this.fuse?.setCollection?.(newValue);
    },
    showAskAiNotesModal(newValue) {
      if (newValue === true) {
        this.aiGeneralSearch.open = false;
      }
    },
  },
  mounted(): void {
    this.fuse = new Fuse(this.membersResults, {
      keys: this.$constants.memberSearchKeys,
      threshold: this.$constants.memberSearchThreshold,
    });
    this.handleSearchModalOpen();
  },

  methods: {
    labelClikedHandler(e: any): void {
      e.stopPropagation();
      this.focusSearchField();
    },
    handleSearchModalOpen(): void {
      this.selectSearchField();
      this.lastSearchQuery();
      this.lastFilterOptions();

      this.restoreAiSearchStateOrSendQuery();

      this.$store.dispatch('setCurrentCardIdForActionEdit', '');
      this.$store.dispatch('setShowDuplicateCardPopup', '');
      this.$store.dispatch('setShowSetParentCardPopup', '');
      if (this.$store.getters.getEmojiPickerDisplayed) {
        this.$store.dispatch('setEmojiPickerDisplayed', '');
      }
    },
    restoreAiSearchStateOrSendQuery(): void {
      if (!this.showAskAiNotesModal && this.lastAiGeneralSearchState.open) {
        this.aiGeneralSearch.open = this.lastAiGeneralSearchState.open;
        this.aiGeneralSearch.generatedText = this.lastAiGeneralSearchState.generatedText;
        this.aiGeneralSearch.genericLinks = this.lastAiGeneralSearchState.genericLinks;
      } else {
        this.sendQuery();
      }
    },
    lastSearchQuery(): void {
      this.searchQuery = this.showAskAiNotesModal
        ? this.$store.getters.getLastAskAiNotesSearchQuery
        : this.$store.getters.getLastSearchQuery;

      if (Object.keys(this.searchResults).length) {
        this.responseQuery = true;
      }
      if (this.searchQuery !== '') {
        this.selectedIndex = 0;
      }
    },
    focusSearchField(): void {
      this.$refs.searchTextInput.focus();
    },
    blurSearchField(): void {
      this.$refs.searchTextInput.blur();
    },
    selectSearchField(): void {
      this.$nextTick(() => {
        if (this.$refs.searchTextInput) {
          this.focusSearchField();
          this.$refs.searchTextInput.select();
        }
      });
    },
    handleInputChange(): void {
      if (this.searchQuery === '') {
        this.$store.dispatch('cancelSearch');
      }
      this.disableMouseEnter();
      this.sendQuery();
      this.scrollToTop();
    },
    handleEnter(): void {
      if (this.showAskAiNotesModal || this.aiGeneralSearch.open) {
        this.askAi();
      }
    },
    handleEsc(): void {
      this.hidePopup();
    },
    sendQuery(): void {
      this.$store
        .dispatch(
          'sendSearchQuery', // CAN'T MAKE ENUM
          {
            query: this.searchQuery,
            advanceOptions: { ...this.searchParams, grouped: false },
          }
        )
        .then(() => {
          this.responseQuery = true;
          if (this.searchQuery === '') {
            this.responseQuery = false;
          }
        });
    },
    hidePopup(): void {
      if (!this.showAskAiNotesModal) {
        this.lastAiGeneralSearchState = {
          open: this.aiGeneralSearch.open,
          generatedText: this.aiGeneralSearch.generatedText,
          genericLinks: this.aiGeneralSearch.genericLinks,
        };
        this.$store.dispatch('setLastSearchQuery', this.searchQuery);
      } else {
        this.$store.dispatch('setLastAskAiNotesSearchQuery', this.searchQuery);
      }

      this.$store.dispatch(StoreActionName.toggleSearchModal, false);
    },
    selectSearchResult(result: any, type: any): void {
      this.selectedIndex = -1;
      if (result && type) {
        if (type === 'project') {
          this.handleResultProject(result);
        } else {
          this.handleResult(result, type);
        }

        if (this.isMobile && this.$store.getters.sidebarExpanded) {
          EventBus.$emit('hideSidebar');
        }
      }
    },
    handleCardOrEpicClick(result: any, type: any) {
      // Redirect to roadmap paywall if epic is clicked and feature is not available
      if (type === ResourceType.Epic && !this.epicFeatureAvailable)
        return this.$router.push({ name: this.$constants.epicsBoardRoute.name });

      const card = this.$store.getters.getCards[result.id] || {};
      this.$store.dispatch('setDisplayedCards', [result.id]);
      const title = card.title || result.title || '';
      const dashedCardTitle = getDashedTitle(title);
      const resourceRoutePath = type == ResourceType.Epic ? this.$constants.epicRoute.path : 'card';

      this.$router.push({
        path: `/${this.teamSlug}/${resourceRoutePath}-${result.id}-${dashedCardTitle}`,
        query: { userNavigated: true },
      });
    },
    goToMemberRoute(route: string) {
      const newRoute = `/${this.teamSlug}/${route}`;
      if (this.$route.path !== newRoute) this.$router.push(newRoute);
    },
    handleMemberClick(result: any, type: any) {
      if (result.id === this.userId) {
        this.goToMemberRoute(`${this.$constants.myWorkRouteName}/${this.myWorkTabId}`);
      } else {
        this.goToMemberRoute(`${type}-${result.id}`);
      }

      this.hidePopup();
    },
    handleNoteClick(result: any) {
      this.$router.push({
        name: this.$constants.routeNames.note,
        params: { noteId: result.id },
      });
      this.hidePopup();
    },
    handleResult(result: any, type: any): void {
      if (type === ResourceType.Member) {
        this.handleMemberClick(result, type);
        return;
      }

      if (type === ResourceType.Note) {
        this.handleNoteClick(result);
        return;
      }

      if (this.favouritesOpened) {
        this.$store.dispatch('setFavouritesOpened', false);
        this.$store.dispatch('setOpenedFavouriteItem', {});
      }
      const resultDashedTitle = getDashedTitle(result.title);
      if (type === 'page' && (this.$route.name === 'page' || this.$route.name === 'newpage')) {
        this.$store.dispatch('setNewPageCreating', false);
        this.$store.dispatch('setCurrentPageID', result.id);
        if (this.$store.getters.getCurrentPageID !== result.id) {
          this.$store.dispatch('setPage', {
            id: result.id,
            title: result.title,
            content: result.excerpt,
            project_id: result.project_id,
            ...(result.archived && { archived: result.archived }),
          });
        }
      }
      if (type === 'board') {
        this.$store.dispatch('setCurrentProjectId', result.project_id);
        this.$store.dispatch('setCurrentBoardId', result.id);
      }
      if ([ResourceType.Card, ResourceType.Epic].includes(type)) {
        this.handleCardOrEpicClick(result, type);
      } else {
        this.$store.dispatch('setDisplayedCards');
        const newRoute = `/${this.teamSlug}/${type}-${result.id}-${resultDashedTitle}`;
        if (this.$route.path !== newRoute) {
          this.$router.push(newRoute);
        }
      }
      this.hidePopup();
    },
    handleResultProject(result: any): void {
      this.$store.dispatch('setDisplayedCards');
      this.setAndOpenProject(result.id);
      this.hidePopup();
    },
    toggleArrayItem(array: any[], value: any, isChecked: boolean) {
      if (isChecked) {
        const index = array.indexOf(value);
        if (index > -1) {
          array.splice(index, 1);
        }
      } else {
        array.push(value);
      }
    },
    typeValueChange(value: any): void {
      this.toggleArrayItem(this.searchParams.types, value.searchKey, value.checked);
      this.storeFilterOptions();
      this.sendQuery();
    },
    statusValueChange(value: any): void {
      this.toggleArrayItem(this.searchParams.statuses, value.id, value.checked);
      this.storeFilterOptions();
      this.sendQuery();
    },
    archivedChange(value: any): void {
      this.searchParams.archived = value;
      this.storeFilterOptions();
      this.sendQuery();
    },
    spaceValueChange(value: any): void {
      this.toggleArrayItem(this.searchParams.spaces, value.id, value.checked);

      this.storeFilterOptions();
      this.sendQuery();
    },
    resetFilters(): void {
      this.searchParams = cloneDeep(defaultFilters);
      if (this.searchQuery) {
        this.sendQuery();
      }
      this.storeFilterOptions();
      this.focusSearchField();
    },
    storeFilterOptions(): void {
      const filterOptions: ISearchModalFilterOptions = {
        searchParamsTypes: this.searchParams.types,
        searchParamsArchived: this.searchParams.archived,
        searchParamsSpaces: this.searchParams.spaces,
        searchParamsStatuses: this.searchParams.statuses,
      };
      browserStorageSetItem(searchModalFilterStorageKey, filterOptions);
    },
    lastFilterOptions(): void {
      const savedFilterOptions = browserStorageGetItem<ISearchModalFilterOptions | null>(
        searchModalFilterStorageKey
      );
      if (savedFilterOptions) {
        this.searchParams.types = savedFilterOptions.searchParamsTypes ?? [];
        this.searchParams.archived = !!savedFilterOptions.searchParamsArchived;
        this.searchParams.statuses = savedFilterOptions.searchParamsStatuses ?? [
          ...defaultStatuses,
        ];
        this.searchParams.spaces = savedFilterOptions.searchParamsSpaces ?? [];
      }
    },
    searchPillDropdownClosed(): void {
      this.focusSearchField();
    },
    mouseMoveHandle(): void {
      this.$emit('mouseMove');
    },
    disableMouseEnter(): void {
      this.$emit('disableMouseEnter');
    },
    scrollToTop(): void {
      this.scrollTop = true;
    },
    escapeRegExp(string: string): string {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },

    async askAi() {
      const isNotesMode = this.showAskAiNotesModal;
      const aiState = isNotesMode ? this.aiNotesSearch : this.aiGeneralSearch;

      if (aiState.loading) return;
      if (this.searchQueryLength < 10) {
        this.$store.dispatch('addUiNotification', {
          message: `${this.translate('askAiQuestionLong')}`,
          status: 'error',
          duration: 4000,
        });
        return;
      }

      if (!isNotesMode && aiState.open && !aiState.loading && this.searchQuery === '') {
        aiState.open = false;
        aiState.generatedText = '';
        aiState.genericLinks = [];
        return;
      }

      aiState.open = true;
      aiState.loading = true;
      aiState.generatedText = '';
      aiState.genericLinks = [];

      const resourceTypes = isNotesMode ? ['note'] : [];
      const response = await this.$store.dispatch('askQuestionAi', {
        question: this.searchQuery,
        resource_types: resourceTypes,
      });

      await this.convertStreamResponse(response, false, aiState).finally(() => {
        aiState.loading = false;
      });
    },

    handleAskAiClick() {
      if (this.aiGeneralSearch.open) {
        this.aiGeneralSearch.open = false;
        this.aiGeneralSearch.generatedText = '';
        this.aiGeneralSearch.genericLinks = [];
      } else {
        this.askAi();
      }
    },

    getMembersResults(query: string) {
      const queriedMembers = this.fuse?.search(query) || [];

      return queriedMembers.map((fuseResult: FuseResult<any>) => ({
        member: fuseResult.item,
      }));
    },
  },
});
</script>

<style lang="scss">
@import '../../assets/scss/typography';
@import '../../assets/scss/mixins/index';
@import '../../assets/scss/breakpoints';
$szFilterIcon: 18px;
$szFilterIconWrapper: $szFilterIcon + 14px;
$wInputRightIcon: 42px;
$szFilterIconIndicator: 6px;
$mwModal: 704px;
.popup-window {
  &--search-window {
    margin-top: $searchSpaceFromTop;
    @apply w-11/12 border border-solid border-important-border bg-important-bg;
    @include tablet {
      @apply w-7/12;
      max-width: $mwModal;
    }

    > div:last-child {
      border-bottom-width: 0px !important;
    }
    .search-input {
      height: $hSearch;
      &__filter-popup {
        right: $wInputRightIcon - $szFilterIconWrapper;
        top: 100%;
        width: 250px;
      }
      &__filter-icon {
        height: $szFilterIconWrapper;
        width: $szFilterIconWrapper;
        border-radius: $spaceXs;
        em {
          height: $szFilterIcon;
          width: $szFilterIcon;
        }
      }
      &__filter-icon-indicator {
        left: $szFilterIconWrapper - $szFilterIconIndicator;
        height: $szFilterIconIndicator;
        width: $szFilterIconIndicator;
        border-radius: 50%;
      }
      &__icon {
        &--left {
          width: $hSearch;
          em {
            height: $szSearchIcon;
            width: $szSearchIcon;
          }
        }
        &--right {
          width: $wInputRightIcon;
        }
      }
      &__field {
        @include textXlNormal();
      }
    }
    .search-results {
      max-height: calc(100vh - #{$searchSpaceFromTop} - #{$searchSpaceFromTop} - #{$hSearch});
      &__no-results {
        padding: {
          top: $spaceTopPrimary;
          right: $spaceRightPrimary;
          bottom: $spaceBottomPrimary;
          left: $spaceLeftPrimary;
        }
        @apply text-text-default;
      }
    }
  }
}

.mobile {
  .popup-window {
    &--search-window {
      margin-top: 8%;
    }
  }
}
</style>
